import { useState } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import { STATUS_DEFINITIONS, getStatusStyle, getStatusOrder } from './statusDefinitions';
import MoneyLabel from '../label/MoneyLabel';

// Compact SVG donut. Visualizes the per-status breakdown by count (default),
// gross volume, or open gross. No chart library required — keeps the bundle lean
// and the visual minimalist.

// Mode keys resolve to a row field on each breakdown entry. The "volume" / "open"
// modes follow the parent card's brutto/netto toggle.
const MODES = (amountMode) => [
  { key: 'count', labelKey: 'Count', field: 'count' },
  {
    key: 'volume',
    labelKey: 'Volume',
    field: amountMode === 'net' ? 'net' : 'gross',
  },
  {
    key: 'open',
    labelKey: 'Open',
    field: amountMode === 'net' ? 'openNet' : 'openGross',
  },
];

const StatusBreakdownDonut = ({
  resource,
  breakdown,
  hideOpenMode,
  amountMode = 'net',
  size = 110,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [mode, setMode] = useState('count');

  const allModes = MODES(amountMode);
  const modes = hideOpenMode ? allModes.filter((m) => m.key !== 'open') : allModes;
  const activeMode = modes.find((m) => m.key === mode) ?? modes[0];

  const definitions = STATUS_DEFINITIONS[resource] || {};
  const orderedKeys = getStatusOrder(resource).filter((s) => definitions[s]);
  const segments = orderedKeys
    .map((status) => {
      const row = breakdown?.find?.((b) => b.status === status);
      if (!row) return null;
      const value = row[activeMode.field] ?? 0;
      const def = definitions[status];
      const { color } = getStatusStyle(colors, def.variant);
      return {
        status,
        value,
        count: row.count,
        net: row.net,
        gross: row.gross,
        openNet: row.openNet,
        openGross: row.openGross,
        labelKey: def.labelKey,
        color,
      };
    })
    .filter(Boolean)
    .filter((s) => s.value > 0);

  const total = segments.reduce((acc, s) => acc + s.value, 0);

  const stroke = 14;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  // Build a cumulative offset for each segment.
  let cumulative = 0;
  const rendered = segments.map((seg) => {
    const fraction = total > 0 ? seg.value / total : 0;
    const dash = circumference * fraction;
    const gap = circumference - dash;
    const offset = -cumulative * circumference;
    cumulative += fraction;
    return { ...seg, dash, gap, offset, fraction };
  });

  return (
    <Box className="flex flex-col gap-2">
      <Box className="flex items-center justify-between flex-wrap gap-2">
        <Typography
          variant="caption"
          sx={{ color: colors.text, opacity: 0.7, fontWeight: 500 }}
        >
          {t('Status breakdown')}
        </Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, v) => v && setMode(v)}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              color: `${colors.text}99`,
              borderColor: colors.selected,
              paddingX: 1,
              paddingY: 0.25,
              fontSize: '0.7rem',
              '&.Mui-selected': {
                backgroundColor: colors.selected,
                color: colors.text,
              },
            },
          }}
        >
          {modes.map((m) => (
            <ToggleButton key={m.key} value={m.key}>
              {t(m.labelKey)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box className="flex items-center gap-3">
        <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
          <svg width={size} height={size}>
            {/* Background ring */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.bgInput}
              strokeWidth={stroke}
              fill="none"
            />
            {/* Segments */}
            {total > 0 &&
              rendered.map((seg) => (
                <Tooltip
                  key={seg.status}
                  title={
                    <Box className="flex flex-col gap-0.5">
                      <span style={{ fontWeight: 600 }}>{t(seg.labelKey)}</span>
                      <span>
                        {t('Count')}: {seg.count}
                      </span>
                      <span>
                        {t('Volume')} ({amountMode === 'net' ? t('netto') : t('brutto')}):{' '}
                        <MoneyLabel
                          money={amountMode === 'net' ? seg.net : seg.gross}
                          variant="caption"
                        />
                      </span>
                      {!hideOpenMode && (
                        <span>
                          {t('Open')} ({amountMode === 'net' ? t('netto') : t('brutto')}):{' '}
                          <MoneyLabel
                            money={amountMode === 'net' ? seg.openNet : seg.openGross}
                            variant="caption"
                          />
                        </span>
                      )}
                    </Box>
                  }
                >
                  <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={seg.color}
                    strokeWidth={stroke}
                    fill="none"
                    strokeDasharray={`${seg.dash} ${seg.gap}`}
                    strokeDashoffset={seg.offset}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    style={{ cursor: 'help', transition: 'stroke-dasharray 0.3s ease' }}
                  />
                </Tooltip>
              ))}
          </svg>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {mode === 'count' ? (
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>
                {total}
              </Typography>
            ) : (
              <MoneyLabel money={total} variant="body2" />
            )}
            <Typography
              variant="caption"
              sx={{
                color: colors.text,
                opacity: 0.5,
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                marginTop: 0.25,
              }}
            >
              {t(activeMode.labelKey)}
            </Typography>
          </Box>
        </Box>
        {/* Legend */}
        <Box className="flex flex-col gap-0.5 flex-1 min-w-0">
          {segments.map((seg) => (
            <Box
              key={seg.status}
              className="flex items-center justify-between gap-2"
            >
              <Box className="flex items-center gap-1.5 min-w-0">
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: seg.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text,
                    opacity: 0.85,
                    fontSize: '0.72rem',
                  }}
                  className="truncate"
                >
                  {t(seg.labelKey)}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: colors.text,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  flexShrink: 0,
                }}
              >
                {mode === 'count' ? (
                  seg.value
                ) : (
                  <MoneyLabel money={seg.value} variant="caption" />
                )}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default StatusBreakdownDonut;
